'use strict';
import { BaseService } from '@narrate/base-sdk';

const HEADER_ENDPOINT_OPTIONS = 'nr_endpoint_opts';
const HEADER_ENDPOINT_PAGESIZE = 'nr_endpoint_pagesize';
const HeaderOptionDoNotReturnMessages = 'do_not_return_messages';

/**
 * The ConversationService manages conversations within Narrate. A conversation
 * is the entire history of messages between two users.
 *
 * Goals and addressed concerns
 *  * Influencers may have a large number of conversations that may not entirely fit
 *    on their mobile devices. 
 *  * Influencers may need to control the sort order of their inbox view
 *  * Influencers are typically NOT conversation starters (initial users in a conversation). Autoreplies don't count.
 *  * An individual is unlikely to have 10,000+ conversations that they start
 *    themselves.
 *  * An individual conversation may contain 100's of messages. All messages may not
 *    fit within a single dynamo db row.
 *  * At present, there isn't a ton of value in storing a message as a row in dynamo.
 *  * Predictable performance characteristics with data growth is important
 *  * Even partitioning of data (sharding / hashing) will be key in promoting
 *    predictable dynamo performance characteristics.
 * 
 * 
 * 
 * Sharding
 * 
 * Each conversation receives a row in dynamodb partitioned by the
 * conversation's initialUserId. In other words, there is a single object for each
 * conversation between two users. Understanding the reasoning behind
 * initialUserId and participantUserId is essential to understanding key
 * behaviors of this class. The rationale is that a user will have less
 * manually initiated conversations. Contrast this with the an influencer user
 * who many have 100K conversations that they did not initiate. So when the
 * data is stored in dyanamo we have chosen to shard on initialUserId.
 *
 * But how does a user get conversations that they did not initiate?
 *
 * There is a GSI on this table which uses the `participantId` as its hash key.
 * This enables us to easily pull conversations by participantId as well. The
 * call to `list` does this automatically, returning two lists of conversations.
 *
 *
 *
 * Indexes
 *
 * How is dynamo setup to sort by fields like `hasUnreadMessages` or `bounty`?
 * There are two indexes on the `conversations` table in dynamo
 * (participantUserConversations (GSI), initialUserConversations (GSI)):
 * ```
 *     {
 *       IndexName: "participantUserConversations",
 *       KeySchema: [
 *         { AttributeName: "participantUserId",         KeyType: "HASH" },
 *         { AttributeName: "participantUserInboxStatus",KeyType: "RANGE" },
 *       ],
 *       ProvisionedThroughput: {
 *         ReadCapacityUnits: 1,
 *         WriteCapacityUnits: 1
 *       },
 *       Projection: {
 *         ProjectionType: "INCLUDE",
 *         NonKeyAttributes: [
 *           'messageCount',
 *           'lastMessage',
 *           'updatedAt',
 *         ]
 *       },
 *     },
 *
 *     {
 *       IndexName: "initialUserConversations",
 *       KeySchema: [
 *         { AttributeName: "initialUserId",           KeyType: "HASH" },
 *         { AttributeName: "initialUserInboxStatus",  KeyType: "RANGE" },
 *       ],
 *       Projection: {
 *         ProjectionType: "INCLUDE",
 *         NonKeyAttributes: [
 *           'messageCount',
 *           'lastMessage',
 *           'updatedAt',
 *         ]
 *       },
 *     },
 * ```
 *  
 * Notice the sort keys `participantUserInboxStatus` and
 * `initialUserInboxStatus` for the participant and initial users,
 * respectively. The inboxStatus field is an integer composed of many
 * conversation status values respective to the user. The mathematical
 * composition of this fiels is flexible, enabling Narrate to manipulate the
 * priority of a conversation. Here is how it is currenty setup:
 *
 * ```
 *  systemOverride * 1000000 + bounty * 1000 + hasUnreadMessages * 100
 * ```
 *
 * There is intentionally a great deal of space between these factors, enabling
 * us to encode additional features into the priority at a later date without
 * changing existing data. 
 * 
 * And, of course, if we need to, we can always sweep through the data with a
 * lambda to enable a different priority scheme altogether.
 *
 * @example
 *    // using CoreService (recommended)
 *    import Core from '@narrate/sdk';
 *
 *    const { Item: conversation } = await Core.api.conversation.get(initialUserId, participantUserId);
 *
 *
 *
 * For more up to date and thorough examples please see integration tests at `src/ConversationService.int.js`
 * There, we run through the gamut of supported use cases and intended behaviors
 *
 */
class ConversationService extends BaseService {

  constructor() {
    super({ configPath: 'services.conversations' });
    this.listState = { pageSize: 30, lastFetchedId: '' };
    this.messagesListState = { pageSize: 50, lowestFetchedMessageNumber: '', highestFetchedMessageNumber: '' };
  }

  /**
   * Creates a new conversation instance with the proposed initialUserId and participantUserId
   *
   * NOTE: In this call `initialUserInboxStatus` and `participantUserInboxStatus`
   * fields are intentionally NOT set by the backend. This allows us to create
   * this empty conversation silently, so that it does not show up in indexes.
   *
   * NOTE: it is possible that a conversation for this pair of users already
   *    exists. If it does, then we will return the conversation that is already
   *    there. And that conversation may have the order of the users swapped.
   * 
   * TODO there is a potential race condition here where two lambdas try to
   * create a conversation with initialUserId and participantUserId swapped
   *  
   * 
   */
  async create(participantUserId) {
    return await this.requestRootResource({
      method: 'post',
      data: { participantUserId }
    });
  }

  async getNewMessages() {
    let params = this.messagesListState;
    const { Items, Count } = await this.requestResource('newMessages', { params });

    if (Count > 0) {
      this.messagesListState.highestFetchedMessageNumber = Items[Items.length - 1].messageNumber;
    }

    return { Items, Count };
  }

  async listMessagesPreviousPage() {
    let params = this.messagesListState;
    const { Items, Count, HasMore } = await this.requestResource('listMessages', { params });

    if (Count > 0) {
      this.messagesListState.lowestFetchedMessageNumber = Items[0].messageNumber;
    }

    return {
      Items,
      Count,
      HasMore
    };
  }

  async list(pageSize = 30) {
    this.listState = { pageSize, lastFetchedId: '' };
    return await this.listNextPage();
  }

  async listNextPage() {
    let params = this.listState;
    const { Items, Count, lastFetchedId } = await this.requestResource('list', { params });
    this.listState.lastFetchedId = lastFetchedId ? lastFetchedId : '';

    return { Items, Count, HasMore: lastFetchedId != null };
  }

  _initConvoMessagesListingState({ initialUserId, participantUserId }, pageSize = 50) {
    this.messagesListState = {
      initialUserId,
      participantUserId,
      pageSize,
      lowestFetchedMessageNumber: '',
      highestFetchedMessageNumber: ''
    };
  }

  async get({ initialUserId, participantUserId }, messagesPageSize = 50) {

    this._initConvoMessagesListingState({ initialUserId, participantUserId }, messagesPageSize);

    const response = await this.requestResource('getWithMsgsPageSize', {
      params: {
        initialUserId,
        participantUserId,
        messagesPageSize
      }
    });

    if (response.Item && response.Item.messages && response.Item.messages.length > 0) {
      this.messagesListState.lowestFetchedMessageNumber = response.Item.messages[0].messageNumber;
      this.messagesListState.highestFetchedMessageNumber = response.Item.messages[response.Item.messages.length - 1].messageNumber;
    }

    return response;
  }

  async delete({ initialUserId, participantUserId }) {
    return await this.requestResource('get', {
      method: 'delete',
      params: {
        initialUserId,
        participantUserId
      }
    });
  }

  async addMessage(message, { initialUserId, participantUserId }, options = { messagesPageSize: 30 }) {
    return await this.requestResource('send', {
      method: 'post',
      data: message,
      params: {
        initialUserId,
        participantUserId,
      },
      headers: {
        [HEADER_ENDPOINT_OPTIONS]: HeaderOptionDoNotReturnMessages,
        [HEADER_ENDPOINT_PAGESIZE]: options.messagesPageSize || 30
      }
    });
  }

  async setUnreadStatus(unreadStatus = false, { messageSequence, initialUserId, participantUserId },
    options = { messagesPageSize: 30 }) {
    return await this.requestResource('unread', {
      method: 'patch',
      data: {
        messageSequence,
        unreadStatus,
      },
      params: {
        initialUserId,
        participantUserId,
      },
      headers: {
        [HEADER_ENDPOINT_OPTIONS]: HeaderOptionDoNotReturnMessages,
        [HEADER_ENDPOINT_PAGESIZE]: options.messagesPageSize || 30
      }
    });
  }

  async priorityPaymentIntent(amount, { initialUserId, participantUserId }) {
    return await this.requestResource('priorityPaymentIntent', {
      method: 'post',
      data: { amount },
      params: {
        initialUserId,
        participantUserId,
      }
    });
  }

  async priorityPaymentConfirmed(txId, { initialUserId, participantUserId }) {
    return await this.requestResource('priorityPaymentConfirmed', {
      method: 'post',
      data: { txId },
      params: {
        initialUserId,
        participantUserId,
      }
    });
  }
}

export default ConversationService;
